import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { useVocab } from "@/lib/vocab";
import { useWebSocket, usePoll, money, fmtTime, fmtDate, STATUS, STATUS_FLOW, missionLabel, todayIn } from "@/lib/helpers";
import { RideSummary, StepBadge, DriverPanel } from "@/components/RideParts";
import { dayIn, nextDay } from "@/lib/ride";
import { firstDay, lastDay, unavShort, unavText } from "@/lib/unavailability";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { TrendingUp, Wallet, Clock, Activity, ArrowRight, BellRing, MapPin, Navigation, CalendarOff } from "lucide-react";
import { AreaChart, Area, PieChart, Pie, Cell, BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import FieldMap from "@/components/FieldMap";
import { toast } from "sonner";

const PALETTE = ["#c9a24a", "#3b82f6", "#10b981", "#8b5cf6", "#ef4444", "#f59e0b"];

function EmployeeHome() {
  const voc = useVocab();
  const [missions, setMissions] = useState([]);
  const load = useCallback(() => api.get("/missions").then((r) => setMissions(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useWebSocket(() => load());
  usePoll(() => load());
  const today = new Date().toISOString().slice(0, 10);
  const todays = missions.filter((m) => m.start?.slice(0, 10) === today && m.status !== "cancelled");

  const advance = async (m) => {
    const next = STATUS_FLOW[Math.min(STATUS_FLOW.indexOf(m.status) + 1, STATUS_FLOW.length - 1)];
    await api.patch(`/missions/${m.id}/status`, { status: next });
    toast.success(`Statut : ${STATUS[next].label}`); load();
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-overline text-accent">{fmtDate(new Date().toISOString())}</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">Mon planning</h1>
      </div>
      {todays.length === 0 && <p className="text-muted-foreground">{`Aucune ${voc.mission} aujourd'hui.`}</p>}
      <div className="space-y-3">
        {todays.map((m) => (
          <div key={m.id} className="bg-card border border-border rounded-2xl shadow-card p-5" data-testid={`emp-mission-${m.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-display font-bold text-lg">{missionLabel(m)}</div>
                <div className="text-sm text-muted-foreground mt-1">{fmtTime(m.start)} → {fmtTime(m.end)}</div>
                {m.location && <div className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="h-3.5 w-3.5" /> {m.location}</div>}
                {m.notes && <div className="text-sm mt-2">{m.notes}</div>}
              </div>
              <StatusBadge status={m.status} />
            </div>
            {m.status !== "completed" && m.status !== "cancelled" && (
              <Button size="sm" className="mt-4 gap-2 rounded-full" onClick={() => advance(m)} data-testid={`advance-${m.id}`}>
                Passer à « {STATUS[STATUS_FLOW[Math.min(STATUS_FLOW.indexOf(m.status) + 1, 3)]].label} » <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// « Mon planning » du chauffeur en transport (lot T4a) : aujourd'hui et demain, jours pris à l'heure des Réglages
// (Monaco), avec le panneau du chauffeur (Reçu, étapes, appels, GPS). La conciergerie garde EmployeeHome.
// Lot T5b : ses indisponibilités (jamais le motif : le serveur ne l'envoie pas au chauffeur), dans chaque journée et
// dans le bloc « Mes indisponibilités à venir ».
function DriverHome() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const [missions, setMissions] = useState([]);
  const [clients, setClients] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [places, setPlaces] = useState([]);
  const [dispatchPhone, setDispatchPhone] = useState("");
  const [unavs, setUnavs] = useState([]);
  const load = useCallback(() => {
    api.get("/unavailabilities").then((r) => setUnavs(r.data)).catch(() => {});
    api.get("/missions").then((r) => setMissions(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/fleet").then((r) => setFleet(r.data)).catch(() => {});
    api.get("/places").then((r) => setPlaces(r.data)).catch(() => {});
    api.get("/ride-contacts/dispatch").then((r) => setDispatchPhone(r.data.phone || "")).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useWebSocket(() => load());
  usePoll(() => load());
  const tz = settings?.timezone || "Europe/Monaco";
  const aujourdhui = todayIn(tz);
  const demain = nextDay(aujourdhui);
  const du = (jour) => missions.filter((m) => m.start && dayIn(m.start, tz) === jour && m.status !== "cancelled")
    .sort((a, b) => a.start.localeCompare(b.start));
  const jours = [["today", "Aujourd'hui", aujourdhui], ["tomorrow", "Demain", demain]];
  const indisposDu = (jour) => unavs.filter((u) => firstDay(u, tz) <= jour && lastDay(u, tz) >= jour);
  const unavLine = "flex items-center gap-2 rounded-xl border border-border bg-muted/60 px-3 py-2 text-sm font-medium";
  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-overline text-accent">{fmtDate(new Date().toISOString())}</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">Mon planning</h1>
      </div>
      {jours.map(([k, titre, jour]) => (
        <section key={k} className="space-y-3" data-testid={`driver-day-${k}`}>
          <h2 className="font-display font-bold text-lg">{titre} <span className="text-sm font-normal text-muted-foreground">· {fmtDate(`${jour}T12:00:00Z`)}</span></h2>
          {indisposDu(jour).map((u) => (
            <div key={u.id} className={unavLine} data-testid={`driver-unav-${k}-${u.id}`}>
              <CalendarOff className="h-4 w-4 shrink-0 text-muted-foreground" /> Indisponible {unavShort(u, tz, jour)}
            </div>
          ))}
          {du(jour).length === 0 && <p className="text-sm text-muted-foreground">Aucune course.</p>}
          {du(jour).map((m) => (
            <div key={m.id} className="bg-card border border-border rounded-2xl shadow-card p-4 md:p-5 min-w-0" data-testid={`driver-ride-${m.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-display font-bold">{clients.find((c) => c.id === m.client_id)?.name || "—"}</span>
                <StepBadge m={m} />
              </div>
              <RideSummary m={m} fleet={fleet} places={places} employeeName="" />
              <DriverPanel m={m} userId={user.id} clientPhone={clients.find((c) => c.id === m.client_id)?.phone}
                dispatchPhone={dispatchPhone} places={places} onChange={load} />
            </div>
          ))}
        </section>
      ))}
      {unavs.length > 0 && (
        <section className="space-y-2" data-testid="driver-unavs">
          <h2 className="font-display font-bold text-lg">Mes indisponibilités à venir</h2>
          {unavs.map((u) => (
            <div key={u.id} className={unavLine} data-testid={`driver-unavs-${u.id}`}>
              <CalendarOff className="h-4 w-4 shrink-0 text-muted-foreground" />
              {unavText(u, tz).replace(/^./, (c) => c.toUpperCase())}
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tint }) {
  return (
    <div className="bg-card border border-border rounded-2xl shadow-card p-5 transition-transform duration-200 hover:-translate-y-0.5">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: `${tint}1a`, color: tint }}><Icon className="h-5 w-5" /></div>
        <div className="text-xs uppercase tracking-overline text-muted-foreground">{label}</div>
      </div>
      <div className="font-display font-black text-3xl mt-3 tracking-tight">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const voc = useVocab();
  const [data, setData] = useState(null);
  const cur = settings?.currency || "EUR";
  const load = useCallback(() => api.get("/dashboard").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { if (user.role !== "employee") load(); }, [user.role, load]);
  useWebSocket(() => user.role !== "employee" && load());
  usePoll(() => user.role !== "employee" && load());
  const remind = async (id) => { try { await api.post(`/invoices/${id}/remind`); toast.success("Relance envoyée"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } };

  if (user.role === "employee") return voc.business === "transport" ? <DriverHome /> : <EmployeeHome />;
  if (!data) return <p className="text-muted-foreground">Chargement…</p>;

  const revData = Object.entries(data.revenue_by_day || {}).sort().slice(-14).map(([d, v]) => ({ d: d.slice(5), v: Math.round(v) }));
  const typeData = Object.entries(data.type_counts || {}).map(([name, value]) => ({ name, value }));
  const loadData = (data.employee_perf || []).map((e) => ({ name: e.name.split(" ")[0], total: e.total, done: e.completed }));

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-overline text-accent">Pilotage · {fmtDate(new Date().toISOString())}</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">Tableau de bord</h1>
      </div>

      {/* Une case par ligne sur téléphone, 2 jusqu'à 1279 px, 4 à partir de 1280 px : un montant à 6 chiffres tient en entier. */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 max-md:grid-cols-1">
        <Kpi icon={Wallet} label="Revenus encaissés" value={money(data.revenue_paid, cur)} tint="#10b981" />
        <Kpi icon={TrendingUp} label={`${voc.Missions} terminées`} value={money(data.revenue_completed, cur)} tint="#c9a24a" />
        <Kpi icon={Clock} label="Revenus attendus" value={money(data.expected_revenue, cur)} tint="#3b82f6" />
        <Kpi icon={Activity} label="En cours" value={data.counts.in_progress} tint="#8b5cf6" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-card border border-border rounded-2xl shadow-card p-6">
          <h3 className="font-display font-bold text-xl mb-1">Revenus encaissés</h3>
          <p className="text-xs text-muted-foreground mb-4">14 derniers jours</p>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={revData.length ? revData : [{ d: "—", v: 0 }]} margin={{ left: -20, right: 8, top: 8 }}>
              <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#c9a24a" stopOpacity={0.35} /><stop offset="100%" stopColor="#c9a24a" stopOpacity={0} /></linearGradient></defs>
              <XAxis dataKey="d" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #eee", fontSize: 12 }} formatter={(v) => money(v, cur)} />
              <Area type="monotone" dataKey="v" stroke="#c9a24a" strokeWidth={2.5} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card border border-border rounded-2xl shadow-card p-6">
          <h3 className="font-display font-bold text-xl mb-4">Répartition par type</h3>
          {typeData.length === 0 ? <p className="text-sm text-muted-foreground">{`Aucune ${voc.mission}.`}</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={typeData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={48} outerRadius={80} paddingAngle={3}>
                  {typeData.map((e, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #eee", fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="flex flex-wrap gap-2 mt-2">
            {typeData.map((e, i) => <span key={i} className="text-xs flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />{e.name}</span>)}
          </div>
        </div>
      </div>

      {/* Field map */}
      <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
        <div className="flex items-center justify-between p-6 pb-3">
          <div>
            <h3 className="font-display font-bold text-xl flex items-center gap-2"><Navigation className="h-5 w-5 text-accent" /> Sur le terrain</h3>
            <p className="text-xs text-muted-foreground mt-1">{`Localisation indicative par ${voc.mission} · GPS temps réel prévu en V2`}</p>
          </div>
        </div>
        <div className="mx-6 mb-6">
          <FieldMap points={data.field} routes={data.routes} />
          {(data.field || []).filter((f) => f.lat && f.lng).length === 0 && (
            <p className="text-xs text-muted-foreground mt-2">{`Aucune position à afficher — les adresses des ${voc.missions} en cours sont géocodées automatiquement.`}</p>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-card border border-border rounded-2xl shadow-card p-6">
          <h3 className="font-display font-bold text-xl mb-4">Aujourd'hui</h3>
          <div className="space-y-1">
            {data.today_missions.length === 0 && <p className="text-sm text-muted-foreground">{`Aucune ${voc.mission} planifiée aujourd'hui.`}</p>}
            {data.today_missions.map((m) => (
              <div key={m.id} className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
                <div><span className="font-medium">{missionLabel(m)}</span><span className="text-sm text-muted-foreground ml-2">{fmtTime(m.start)}–{fmtTime(m.end)}</span></div>
                <StatusBadge status={m.status} />
              </div>
            ))}
          </div>
          {data.late_missions.length > 0 && (
            <div className="mt-4 p-3 rounded-xl border border-destructive/30 bg-destructive/10 text-sm text-destructive" data-testid="late-alert">
              ⚠ {data.late_missions.length}{` ${voc.mission}(s) en retard.`}
            </div>
          )}
        </div>
        <div className="bg-card border border-border rounded-2xl shadow-card p-6">
          <h3 className="font-display font-bold text-xl mb-4">{`Charge par ${voc.employee}`}</h3>
          {loadData.length === 0 ? <p className="text-sm text-muted-foreground">{`Aucun ${voc.employee}.`}</p> : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={loadData} margin={{ left: -22, right: 8 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #eee", fontSize: 12 }} cursor={{ fill: "#00000008" }} />
                <Bar dataKey="total" radius={[6, 6, 0, 0]} fill="#3b82f6" />
                <Bar dataKey="done" radius={[6, 6, 0, 0]} fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl shadow-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-xl">Factures impayées</h3>
          {data.unpaid_invoices?.length > 0 && <span className="text-sm text-amber-600 font-medium">{money(data.unpaid_total, cur)} en attente</span>}
        </div>
        <div className="divide-y divide-border">
          {(!data.unpaid_invoices || data.unpaid_invoices.length === 0) && <p className="text-sm text-muted-foreground">Aucune facture en attente de paiement. 🎉</p>}
          {data.unpaid_invoices?.map((u) => (
            <div key={u.id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`unpaid-${u.id}`}>
              {/* Sur téléphone : numéro et client sur toute la largeur ; montant et « Relancer » en dessous. */}
              <div className="min-w-0 flex-1 max-md:basis-full">
                <span className="font-mono font-medium whitespace-nowrap">{u.number}</span>
                <span className="text-sm text-muted-foreground ml-2">{u.client_name}</span>
                {u.reminder_count > 0 && <span className="text-xs text-muted-foreground ml-2">· {u.reminder_count} relance(s)</span>}
              </div>
              <span className="font-medium">{money(u.total_ttc, cur)}</span>
              <Button size="sm" variant="outline" className="gap-1.5 rounded-full" onClick={() => remind(u.id)} data-testid={`dash-remind-${u.id}`}>
                <BellRing className="h-3.5 w-3.5 text-amber-600" /> Relancer
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
